import { Autocomplete, Box, Button, FormControl, Input, InputLabel, MenuItem, TableFooter, Select, Stack, Table, TablePagination, Typography, Grid } from '@mui/material'
import React, { useRef, useEffect, useState } from 'react'
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import ActionsBar from '../../../components/ActionsBar';
import { Download, FilterAlt } from '@mui/icons-material'
import TextField from '@mui/material/TextField/TextField';
import Paper from '@mui/material/Paper'
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import PaymentItem from './components/PaymentItem';
import { styled } from '@mui/material/styles'
import { useAtom, useAtomValue } from 'jotai';
import { authAtom, staffsAtom, tokenAtom, serviceGroupsAtom } from '../../../actions';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmPaymentModal from './modals/ConfirmPaymentModal';
import DateRangeModal from './modals/DateRangeModal';
import dayjs from 'dayjs';
import { hasPermission, showConfirmationToast, convertMoney, PAYMENT_METHOD, getOrderId } from '../../../utils/utils';
import * as XLSX from 'xlsx'
import { MultiSelect } from "react-multi-select-component";
import FilterModal from './components/FilterModal';
import ActiveFilters from './components/ActiveFilters';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,

    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const Screen = () => {
    const [total, setTotal] = useState(0)
    const [totalPayment, setTotalPayment] = useState(0)
    const [payments, setPayments] = useState([])
    const [token] = useAtom(tokenAtom)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [keyword, setKeyword] = useState('')
    const [selectedStaffs, setSelectedStaffs] = useState([])
    const [method, setMethod] = useState(0)
    const [staffs] = useAtom(staffsAtom)
    const [confirmPaymentModal, setConfirmPaymentModal] = useState(null)
    const [dateRangeModal, setDateRangeModal] = useState(false)
    const [startDate, setStartDate] = useState(dayjs().subtract(90, 'days').format())
    const [endDate, setEndDate] = useState(dayjs().format())
    const authUser = useAtomValue(authAtom)
    const toastId = useRef(null)
    const timeoutRef = useRef(null);
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const serviceGroups = useAtomValue(serviceGroupsAtom);
    const [serviceGroup, setServiceGroup] = useState(null);
    const [serviceType, setServiceType] = useState('all');
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setDebouncedKeyword(keyword);
        }, 500);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [keyword]);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Token ${token}`
            setPayments([])
            setTotal(0)
            setTotalPayment(0)

            axios.post('/booking/payments/payment_list/', {
                offset: page * rowsPerPage, limit: rowsPerPage,
                keyword: debouncedKeyword.replace(/#/g, ''),
                staffs: selectedStaffs.length > 0 ? selectedStaffs.map((item) => item.value) : null,
                method: method > 0 ? method : null,
                service_group: serviceGroup,
                service_type: serviceType !== 'all' ? serviceType : null,
                startDate: dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                endDate: dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
            }, {})
                .then((response) => {
                    if (response && response.data) {
                        setPayments(response.data.payments)
                        setTotal(response.data.total)
                        setTotalPayment(response.data.totalPayment ?? 0)
                    } else {
                        setPayments([])
                        setTotal(0)
                    }
                })
                .catch((error) => {
                    console.log({ error })
                    setPayments([])
                    setTotal(0)
                })
        }
    }, [token, page, rowsPerPage, debouncedKeyword, selectedStaffs, method, startDate, endDate, serviceGroup, serviceType])

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeKeyword = (event) => {
        setKeyword(event.target.value)
        setPage(0)
    }

    const onConfirmPayment = (payment) => {
        axios.post('/booking/payments/confirm_payment/', {
            payment_id: payment.id, lead_order: payment.lead_order, payment_date: payment.payment_date,
            amount: payment.amount, note: payment.note,
            payment_type: payment.payment_type, payment_method: payment.payment_method
        })
            .then((response) => {
                if (response && response.data) {
                    const filter = payments.filter((item) => item.id === payment.id)
                    if (filter && filter.length > 0) {
                        const index = payments.indexOf(filter[0])
                        const newPayments = payments
                        newPayments.splice(index, 1, response.data)
                        setPayments(newPayments)
                    }
                    toast.success('Xác nhận thành công')
                    setConfirmPaymentModal(null)
                } else {
                    toast.error('Cập nhật thất bại')
                }
            })
            .catch((error) => {
                console.log({ error })
                if (error && error.response && error.response.data && error.response.data.message)
                    toast.error(error.response.data.message)
                else
                    toast.error('Cập nhật thất bại')
            })
    }

    const onChangeRange = (range) => {
        setStartDate(range.startDate)
        setEndDate(range.endDate)
        setDateRangeModal(false)
    }

    const onDownload = () => {
        showConfirmationToast(`Chắc chắn tải về danh sách thanh toán từ ${dayjs(startDate).format('DD/MM/YYYY')} đến ${dayjs(endDate).format('DD/MM/YYYY')}?`,
            () => {
                setTimeout(() => {
                    toastId.current = toast.loading("Đang tải dữ liệu")
                    axios.post('/booking/payments/download_payment_list/', {
                        keyword: debouncedKeyword.replace(/#/g, ''), staffs: selectedStaffs.length > 0 ? selectedStaffs.map((item) => item.value) : null,
                        method: method > 0 ? method : null,
                        startDate: dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'), endDate: dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
                    }, {})
                        .then((response) => {
                            try {
                                if (response && response.data) {
                                    const reports = response.data.map((item) => {
                                        return ({
                                            'id': item.lead_order.user.id,
                                            'full_name': item.lead_order.user.full_name,
                                            'username': item.lead_order.user.username,
                                            'createdBy': item.created_by.full_name,
                                            'order': getOrderId(item.lead_order.id),
                                            'approvedBy': item.approved_by ? item.approved_by.full_name : '',
                                            'totalPrice': item.amount ?? 0,
                                            'method': PAYMENT_METHOD[item.payment_method],
                                            'date': dayjs(item.payment_date).format('HH:mm DD/MM/YYYY'),
                                            'sale1': `${item.lead_order.sale1 ? item.lead_order.sale1.full_name : ''}`,
                                            'sale1Comission': item.comission_sale1 ?? 0,
                                            'sale2': `${item.lead_order.sale2 ? item.lead_order.sale2.full_name : ''}`,
                                            'sale2Comission': item.comission_sale2 ?? 0,
                                            'telesale': `${item.lead_order.telesale ? item.lead_order.telesale.full_name : ''}`,
                                            'telesaleComission': item.comission_telesale ?? 0,
                                            'note': item.note,
                                            'shop': item.lead_order.shop ? item.lead_order.shop.name : ''
                                        })
                                    })
                                    const worksheet = XLSX.utils.json_to_sheet(reports, { origin: 'A2' });
                                    const headers = ['ID', "Khách hàng", 'Số điện thoại', 'Mã đơn hàng', 'Người nhập', 'Người xác nhận', 'Số tiền thanh toán', 'Hình thức thanh toán', 'Ngày thanh toán', 'Sale chính', 'Hoa hồng sale chính', 'Sale phụ', 'Hoa hồng sale phụ', 'Telesale', 'Hoa hồng telesale', 'Chú thích', 'Cửa hàng']
                                    worksheet['!merges'] = [
                                        { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length } },
                                    ]
                                    XLSX.utils.sheet_add_aoa(worksheet, [[`Danh sách thanh toán từ ${dayjs(startDate).format('DD/MM/YYYY')} đến ${dayjs(endDate).format('DD/MM/YYYY')}`]], { origin: "A1" });
                                    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });
                                    const workbook = XLSX.utils.book_new();
                                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet 1');
                                    XLSX.writeFile(workbook, `Danh sách thanh toán.xlsx`);

                                    toast.dismiss(toastId.current)
                                    toast.success('Tải về thành công')
                                }
                            } catch (error) {
                                toast.dismiss(toastId.current)
                                toast.error('Lỗi tải về')
                            }
                        })
                        .catch((error) => {
                            console.log({ error })
                            toast.dismiss(toastId.current)
                            toast.error('Lỗi tải về')
                        })
                }, 500);
            }, () => { })
    }

    const staffOptions = staffs.map((item) => ({ label: item.full_name, value: item.id }))

    const handleClearFilter = (type, value) => {
        switch (type) {
            case 'date':
                setStartDate(dayjs().subtract(90, 'days').format());
                setEndDate(dayjs().format());
                break;
            case 'method':
                setMethod(0);
                break;
            case 'staff':
                setSelectedStaffs(selectedStaffs.filter(staff => staff.value !== value));
                break;
            case 'serviceGroup':
                setServiceGroup(null);
                break;
            case 'serviceType':
                setServiceType('all');
                break;
            default:
                break;
        }
    };

    if (!(authUser && hasPermission(authUser, ['view_payment', 'write_payment', 'view_payment_all']))) {
        return <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', margin: '32px' }}>Không có quyền truy cập</Stack>
    }

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '100%', width: '100%' }}>
            <ActionsBar title='Danh sách thanh toán' subTitle={`Tìm thấy: ${total} thanh toán`} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', px: 3, py: 2 }}>
                <FormControl sx={{ width: '300px' }} variant='outlined'>
                    <InputLabel>Tên khách hàng, số điện thoại</InputLabel>
                    <Input
                        value={keyword} onChange={handleChangeKeyword}
                        placeholder="Tên khách hàng, số điện thoại"
                    />
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<FilterAlt />}
                        onClick={() => setFilterModalOpen(true)}
                    >
                        Bộ lọc
                    </Button>
                    {hasPermission(authUser, ['export_report_payment']) && <Button onClick={onDownload}><Download /></Button>}
                </Box>
            </Box>
            <ActiveFilters
                startDate={startDate}
                endDate={endDate}
                method={method}
                selectedStaffs={selectedStaffs}
                serviceGroup={serviceGroup}
                serviceType={serviceType}
                serviceGroups={serviceGroups}
                onClearFilter={handleClearFilter}
            />
            <Box sx={{ display: "flex", width: '100%', marginTop: '16px', padding: '0px' }}>
                <Paper sx={{ width: '100%', padding: '0px 0px' }}>
                    <Stack direction='row' justifyContent='flex-end' spacing='16px'>
                        <TablePagination
                            rowsPerPageOptions={[20, 30, 40, 50]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                    <TableContainer component={Paper} sx={{ overflowY: 'scroll', width: '100%', overflowX: 'scroll', maxHeight: 'calc(100vh - 280px)' }}>
                        <Table stickyHeader aria-label="simple table">
                            <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>

                                <TableRow>
                                    <StyledTableCell sx={{ minWidth: { md: '120px' } }}>Khách hàng</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Người nhập</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Người xác nhận</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Đơn hàng</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Số tiền thanh toán</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Hình thức thanh toán</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Ngày thanh toán</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Sale1</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Sale2</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Telesale</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Chú thích</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>Cửa hàng</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                </TableRow>
                                <TableRow>
                                    <StyledTableCell sx={{ minWidth: { md: '120px' } }} colSpan={4}>Tổng</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}>{`${convertMoney(totalPayment, true)}`}</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                    <StyledTableCell align="right" sx={{ minWidth: { md: '120px' } }}></StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((payment) => (
                                    <PaymentItem
                                        key={payment.id.toString()}
                                        payment={payment}
                                        onConfirmPayment={() => setConfirmPaymentModal(payment)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                {confirmPaymentModal !== null &&
                    <ConfirmPaymentModal
                        open={confirmPaymentModal !== null}
                        onAddPayment={onConfirmPayment}
                        payment={confirmPaymentModal}
                        handleClose={() => {
                            setConfirmPaymentModal(null)
                        }}
                    />
                }
                <DateRangeModal
                    open={dateRangeModal}
                    startDate={startDate}
                    endDate={endDate}
                    onConfirm={onChangeRange}
                    handleClose={() => {
                        setDateRangeModal(false)
                    }}
                />
            </Box>
            <FilterModal
                open={filterModalOpen}
                handleClose={() => setFilterModalOpen(false)}
                startDate={startDate}
                endDate={endDate}
                setDateRangeModal={onChangeRange}
                method={method}
                setMethod={setMethod}
                selectedStaffs={selectedStaffs}
                setSelectedStaffs={setSelectedStaffs}
                staffOptions={staffOptions}
                hasPermission={hasPermission}
                authUser={authUser}
                serviceGroup={serviceGroup}
                setServiceGroup={setServiceGroup}
                serviceType={serviceType}
                setServiceType={setServiceType}
                serviceGroups={serviceGroups}
            />
        </Box>
    )
}

export default Screen